// Secciones del detalle de una venta cerrada, las mismas para Mis ventas del vendedor y Ventas del Admin VL.
import { createContext, useContext, type ReactNode } from 'react';
import { Badge, CopyableId } from '@/kit';
import type { SaleLine, SalePromotionInfo } from '@/api/services/retail-wizard/contract';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { formatMXN } from '@/shared/lib/format/money';
import { DetailCard, DetailKeyValue } from '@/shared/patterns/DetailCard';
import { PaymentBreakdown } from '@/shared/patterns/SalePayments/PaymentBreakdown';
import {
    NO_CHARGE_LABEL,
    isNoCharge,
    type PaymentLine,
} from '@/shared/lib/format/salePaymentLines';
import { hasEquipment } from '@/shared/patterns/SaleTicket';
import {
    receiptPaymentSummary,
    saleAmounts,
    saleReceipt,
    type SaleAmounts,
} from '@/shared/lib/format/saleReceipt';
import {
    SaleBenefitsOutlet,
    SaleLineCard,
    toSlotSale,
    type SaleSlots,
} from '@/shared/patterns/SaleReceipt';
import { financierChipOf } from '../../store/lib/storeFormat';
import {
    EXTERNAL_FOLIO_NOTE,
    financierKeyOf,
    financierName,
    paymentMethodLabel,
    PENDING_CELL,
    saleTypeLabel,
} from '../lib/mySalesFormat';
import { formatStorage } from '@/shared/lib/format/capacity';

export const DETAIL_SECTION_TITLE =
    'mb-1 mt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';

/** Rótulo de la fila de 78px: la palabra que no cabe se parte con guion en vez de encimarse. */
const FLAT_LABEL =
    'hyphens-auto text-[10px] uppercase tracking-[0.04em] text-muted-foreground [overflow-wrap:anywhere]';

const FIRST_SECTION_TITLE =
    'mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';

/** Lo que el detalle lee de la venta, igual si viene de Mis ventas o del backoffice. */
export interface SaleDetailFacts extends SalePromotionInfo {
    saleId: string;
    folio: string;
    soldAt: string;
    storeName: string | null;
    zoneName: string | null;
    /** Vendedor como lo nombra cada rol. */
    sellerLabel: string;
    type: string;
    externalProvider: string | null;
    creditId: string | null;
    paymentMethod: string | null;
    cashConfirmed?: boolean;
    /** Desglose del cobro; `null` en ventas anteriores al campo, que caen al método único. */
    payments?: PaymentLine[] | null;
    amountCents: number;
    downPaymentCents: number | null;
    equipmentPriceCents: number | null;
    lines?: SaleLine[];
    /** `null` en la venta sin teléfono. */
    sku: string | null;
    imei: string | null;
    productBrand: string | null;
    productModel: string | null;
    productCapacity: string | null;
}

/** `card` pinta las secciones como las tarjetas de la ficha de Cartera; `flat` es la de Mis ventas. */
export type SaleDetailLayout = 'flat' | 'card';

const SaleDetailLayoutContext = createContext<SaleDetailLayout>('flat');

export const SaleDetailLayoutProvider = SaleDetailLayoutContext.Provider;

function useCardLayout(): boolean {
    return useContext(SaleDetailLayoutContext) === 'card';
}

/** El título y el cuerpo de una sección, en el armado que toque. */
function Section({
    title,
    first,
    children,
}: {
    title: string;
    first?: boolean;
    children: ReactNode;
}) {
    if (useCardLayout()) return <DetailCard title={title}>{children}</DetailCard>;
    return (
        <>
            <div className={first ? FIRST_SECTION_TITLE : DETAIL_SECTION_TITLE}>{title}</div>
            {children}
        </>
    );
}

export function DetailRow({ label, value }: { label: string; value: ReactNode }) {
    if (useCardLayout()) return <DetailKeyValue label={label} value={value} plain />;
    return (
        <div className="grid grid-cols-[78px_1fr] gap-1.5 py-[3px] text-[11.5px]">
            <span lang="es" className={FLAT_LABEL}>
                {label}
            </span>
            <span className="break-words tabular-nums">{value}</span>
        </div>
    );
}

/** Fila sin fuente de datos: se ve en guion, con su porqué al pasar el cursor. */
export function PendingDetailRow({ label, reason }: { label: string; reason: string }) {
    if (useCardLayout()) return <DetailKeyValue label={label} note={reason} />;
    return (
        <div
            title={reason}
            className="grid grid-cols-[78px_1fr] gap-1.5 py-[3px] text-[11.5px] opacity-60"
        >
            <span lang="es" className={FLAT_LABEL}>
                {label}
            </span>
            <span className="text-muted-foreground">{PENDING_CELL}</span>
        </div>
    );
}

/** Nombre de la financiera externa, o `null` si la venta no la tiene. */
function externalFinancier(sale: Pick<SaleDetailFacts, 'type' | 'externalProvider'>) {
    return sale.externalProvider
        ? financierName(financierKeyOf(sale.type, sale.externalProvider))
        : null;
}

/** Forma de pago y método de cobro del comprobante: «Contado · Efectivo». */
export function salePaymentSummary(
    sale: Pick<SaleDetailFacts, 'type' | 'externalProvider' | 'paymentMethod'>,
): string | null {
    return receiptPaymentSummary({
        type: sale.type,
        paymentMethod: sale.paymentMethod,
        financier: externalFinancier(sale),
    });
}

/** Folio, fecha, tienda, zona y vendedor. */
export function SaleFactsSection({ sale }: { sale: SaleDetailFacts }) {
    return (
        <Section title="Detalles de la venta" first>
            <DetailRow
                label="Folio"
                value={<span className="break-all font-mono">{sale.folio}</span>}
            />
            <DetailRow label="Fecha y hora" value={formatDateTimeMx(sale.soldAt)} />
            <DetailRow label="Tienda" value={sale.storeName ?? PENDING_CELL} />
            <DetailRow label="Zona" value={sale.zoneName ?? PENDING_CELL} />
            <DetailRow label="Vendedor" value={sale.sellerLabel} />
        </Section>
    );
}

/** Folio de la financiera: el `creditId` copiable de la propia (DEC-012); el de la externa no existe. */
function FinancierFolioRow({ sale }: { sale: SaleDetailFacts }) {
    if (sale.creditId) {
        return (
            <DetailRow
                label="Folio financiera"
                value={
                    <CopyableId
                        label="Folio financiera"
                        value={sale.creditId}
                        className="[&>span]:sr-only"
                    />
                }
            />
        );
    }
    return <PendingDetailRow label="Folio financiera" reason={EXTERNAL_FOLIO_NOTE} />;
}

/** Tipo, financiera y su folio; la ficha de Cartera suma el método de cobro y la confirmación. */
export function SalePaymentSection({ sale }: { sale: SaleDetailFacts }) {
    const plainCash = sale.type === 'CASH' && !sale.externalProvider;
    const chip = financierChipOf(sale.type, sale.externalProvider);
    const financier = financierName(financierKeyOf(sale.type, sale.externalProvider));
    const card = useCardLayout();
    const financierValue =
        financier === PENDING_CELL ? (
            PENDING_CELL
        ) : (
            <Badge variant={chip.variant} className={card ? undefined : 'px-2 py-0 text-[10px]'}>
                {financier}
            </Badge>
        );

    // Mis ventas es la `.info-row` del mock: tipo, financiera y folio, con guion en el contado.
    if (!card) {
        return (
            <Section title="Forma de pago">
                <DetailRow label="Tipo" value={saleTypeLabel(sale.type)} />
                <DetailRow label="Financiera" value={plainCash ? PENDING_CELL : financierValue} />
                {plainCash ? (
                    <DetailRow label="Folio financiera" value={PENDING_CELL} />
                ) : (
                    <FinancierFolioRow sale={sale} />
                )}
                {sale.payments && <PaymentBreakdown lines={sale.payments} className="mt-2" />}
            </Section>
        );
    }

    return (
        <Section title="Forma de pago">
            <DetailRow label="Tipo" value={saleTypeLabel(sale.type)} />
            {plainCash ? null : (
                <>
                    <DetailRow label="Financiera" value={financierValue} />
                    <FinancierFolioRow sale={sale} />
                </>
            )}
            {sale.payments ? (
                <PaymentBreakdown lines={sale.payments} className="mt-2" />
            ) : (
                <>
                    <DetailRow
                        label="Método de cobro"
                        value={
                            isNoCharge(sale.paymentMethod, sale.amountCents)
                                ? NO_CHARGE_LABEL
                                : paymentMethodLabel(sale.paymentMethod)
                        }
                    />
                    {sale.cashConfirmed === undefined ? null : (
                        <DetailRow
                            label="Confirmado"
                            value={
                                sale.cashConfirmed
                                    ? 'El efectivo se confirmó en caja'
                                    : 'Sin confirmar en caja'
                            }
                        />
                    )}
                </>
            )}
        </Section>
    );
}

function money(cents: number): string {
    return formatMXN(cents / 100);
}

function LineAmountRows({ sale, amounts }: { sale: SaleDetailFacts; amounts: SaleAmounts }) {
    return (
        <>
            {hasEquipment(sale) && (
                <DetailRow label="Equipo" value={money(amounts.equipmentCents)} />
            )}
            {amounts.accessoriesCents > 0 && (
                <DetailRow label="Accesorios" value={money(amounts.accessoriesCents)} />
            )}
            {amounts.totalCents !== sale.amountCents && (
                <DetailRow label="Total de la venta" value={money(amounts.totalCents)} />
            )}
        </>
    );
}

/** Importes: por líneas (equipo, accesorios) o, en la venta vieja, el precio del equipo. */
export function SaleAmountsSection({
    sale,
    children,
}: {
    sale: SaleDetailFacts;
    /** Filas propias del rol al final de la sección. */
    children?: ReactNode;
}) {
    const withEquipment = hasEquipment(sale);
    const amounts = saleAmounts(sale.lines, withEquipment);
    if (!useCardLayout()) return <FlatAmounts sale={sale} amounts={amounts} />;
    return (
        <Section title="Importes">
            {amounts ? (
                <LineAmountRows sale={sale} amounts={amounts} />
            ) : !withEquipment ? null : (
                <DetailRow
                    label="Precio equipo"
                    value={
                        sale.equipmentPriceCents === null ? (
                            <span className="text-muted-foreground">{PENDING_CELL}</span>
                        ) : (
                            <span className="font-semibold">{money(sale.equipmentPriceCents)}</span>
                        )
                    }
                />
            )}
            <DetailRow
                label={amounts ? 'Total cobrado' : 'Cobrado en caja'}
                value={<span className="font-semibold">{money(sale.amountCents)}</span>}
            />
            {sale.downPaymentCents === null ? null : (
                <DetailRow label="Enganche" value={money(sale.downPaymentCents)} />
            )}
            {children}
        </Section>
    );
}

/** Importes de Mis ventas como el mock: el precio del equipo (si lo hay) y los accesorios. */
function FlatAmounts({ sale, amounts }: { sale: SaleDetailFacts; amounts: SaleAmounts | null }) {
    const equipmentCents = amounts?.equipmentCents ?? sale.equipmentPriceCents;
    return (
        <Section title="Importes">
            {hasEquipment(sale) && (
                <DetailRow
                    label="Precio equipo"
                    value={
                        equipmentCents === null ? (
                            <span className="text-muted-foreground">{PENDING_CELL}</span>
                        ) : (
                            <span className="font-semibold">{money(equipmentCents)}</span>
                        )
                    }
                />
            )}
            {amounts && amounts.accessoriesCents > 0 ? (
                <DetailRow label="Accesorios" value={money(amounts.accessoriesCents)} />
            ) : null}
        </Section>
    );
}

/** Productos de la venta con su promo por línea y, al pie, lo que se lleva el cliente. */
export function SaleLinesSection({ sale, slots }: { sale: SaleDetailFacts; slots: SaleSlots }) {
    const rows = saleReceipt(sale.lines)?.rows;
    if (!rows) return null;
    const slotSale = toSlotSale(
        sale,
        rows.map((r) => r.line),
    );
    return (
        <Section title="Productos de la venta" first>
            <ul aria-label="Productos de la venta" className="mb-3 flex flex-col gap-1.5">
                {rows.map((row) => (
                    <SaleLineCard
                        key={row.key}
                        row={row}
                        sale={slotSale}
                        linePromotion={slots.linePromotion}
                        giftName={slots.giftName}
                        compact
                    />
                ))}
            </ul>
            <SaleBenefitsOutlet
                slot={slots.saleBenefits}
                sale={slotSale}
                className="mb-3 text-[11px]"
            />
        </Section>
    );
}

/** Equipo como quedó en la venta: SKU, marca, modelo y almacenamiento; sin teléfono no se pinta. */
export function SoldEquipmentSection({
    sale,
    children,
}: {
    sale: SaleDetailFacts;
    children?: ReactNode;
}) {
    if (!hasEquipment(sale)) return null;
    return (
        <Section title="Equipo vendido" first>
            <DetailRow label="SKU" value={<span className="font-mono">{sale.sku}</span>} />
            <DetailRow label="Marca" value={sale.productBrand ?? PENDING_CELL} />
            <DetailRow label="Modelo" value={sale.productModel ?? PENDING_CELL} />
            <DetailRow
                label="Almacenamiento"
                value={formatStorage(sale.productCapacity) || PENDING_CELL}
            />
            {children}
        </Section>
    );
}
